'use client';

import { Children, ReactNode, useMemo, useRef } from 'react';
import { useWheelToHorizontalScroll } from '@/hooks/useWheelToHorizontalScroll';

type Props = {
  children: ReactNode;
  /**
   * Gap between adjacent cards in pixels. Matches `MarqueeCardsRow`'s default
   * so switching the autoscroll toggle doesn't shift the layout.
   */
  gap?: number;
};

/**
 * Wheel-scrollable row of cards. Same card layout as `MarqueeCardsRow` —
 * cards keep their intrinsic width and stretch to the tallest one — but the
 * row scrolls horizontally under user control instead of auto-marqueeing.
 * No edge-fade gradient here (per spec), and vertical wheel ticks translate
 * to horizontal scroll via `useWheelToHorizontalScroll`.
 */
export function ScrollingCardsRow({ children, gap = 24 }: Props) {
  const items = useMemo(() => Children.toArray(children), [children]);
  const railRef = useRef<HTMLDivElement>(null);
  useWheelToHorizontalScroll(railRef);

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      ref={railRef}
      className="flex w-full items-stretch overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      {items.map((child, index) => (
        <div
          key={index}
          className="flex h-auto shrink-0 items-stretch"
          style={{ marginRight: index === items.length - 1 ? 0 : gap }}
        >
          {child}
        </div>
      ))}
    </div>
  );
}
